import { patchState } from '@ngrx/signals';
import { addEntity, removeEntity, updateEntity } from '@ngrx/signals/entities';
import { notificationActionMap, notificationLevelMap } from '~/app/constants/notificationLevelMap';
import { NotificationType } from '~/app/enums/NotificationId';
import { NotificationLevel } from '~/app/enums/NotificationLevel';
import { TherapyJobNotification } from '~/app/models/TherapyJobNotification';
import { TherapyJobV2 } from '~/app/models/TherapyJobV2';
import { notificationId } from './notificationId';

/**
 * Builder class for creating therapy job notifications
 */
export class NotificationBuilder {
  private _type: NotificationType;
  private _showAlarmCenter = false;
  private _level: NotificationLevel;
  private _timestamp: number | null | undefined;
  private _translationParams: Record<string, unknown> = {};
  private readonly _therapyJob: Partial<TherapyJobV2>;

  constructor(therapyJob: Partial<TherapyJobV2>) {
    this._therapyJob = therapyJob;
  }

  /**
   * Set the notification type
   */
  type(type: NotificationType): this {
    this._type = type;

    return this;
  }

  /**
   * Set whether to show in alarm center
   */
  showAlarmCenter(show: boolean): this {
    this._showAlarmCenter = show;
    return this;
  }

  /**
   * Set the level; defaults to the level of the web UI-calculated type
   */
  level(level: NotificationLevel): this {
    this._level = level;
    return this;
  }

  /**
   * Set the raise time (epoch ms, `null` = unknown); defaults to the time of the job's last event
   */
  timestamp(timestamp: number | null): this {
    this._timestamp = timestamp;
    return this;
  }

  /**
   * Set translation parameters
   */
  translationParams(params: Record<string, unknown>): this {
    this._translationParams = params;
    return this;
  }

  /**
   * Add a single translation parameter
   */
  addTranslationParam(key: string, value: unknown): this {
    this._translationParams[key] = value;
    return this;
  }

  /**
   * Build the notification object
   */
  build(): TherapyJobNotification {
    const id = notificationId(this._type, this._therapyJob.id);
    const level = this._level || notificationLevelMap[this._type] || NotificationLevel.INFO;

    return {
      id,
      type: this._type,
      timestamp: this._timestamp === undefined ? this._therapyJob?.event?.timestamp : this._timestamp,
      therapyJobId: this._therapyJob?.id,
      messages: {
        card: `Notification${this._type}Card`,
        detail: {
          title: `Notification${this._type}DetailTitle`,
          message: `Notification${this._type}DetailMessage`
        }
      },
      translationParams: this._translationParams,
      level,
      showAlarmCenter: this._showAlarmCenter,
      patient: {
        firstName: this._therapyJob?.patient?.firstName || null,
        lastName: this._therapyJob?.patient?.lastName || null,
        careUnit: this._therapyJob?.patient?.careUnit || null
      },
      action: notificationActionMap[this._type]
    };
  }

  /**
   * Build and patch the notification to the store based on condition
   * @param condition - Whether the notification should be shown
   * @param store - The notification store
   * @param notified - Whether the notification already exists
   */
  buildAndPatch(condition: boolean, store: any, notified: boolean): void {
    const notification = this.build();

    if (condition && !notified) {
      patchState(store, addEntity(notification));
      return;
    }

    if (condition && notified) {
      const prevNotification = store.entityMap()[notification.id];
      const showInAlarmCenter = notification.showAlarmCenter !== prevNotification.showAlarmCenter;
      const levelChange = notification.level !== prevNotification.level;
      const paramsChange =
        JSON.stringify(notification.translationParams) !== JSON.stringify(prevNotification.translationParams);

      if (showInAlarmCenter || levelChange || paramsChange) {
        patchState(store, updateEntity({ id: notification.id, changes: notification }));
      }
      return;
    }

    if (!condition && notified) {
      patchState(store, removeEntity(notification.id));
    }
  }
}

/**
 * Create a new notification builder for a therapy job
 */
export function createNotification(therapyJob: Partial<TherapyJobV2>): NotificationBuilder {
  return new NotificationBuilder(therapyJob);
}
